import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../ui/button";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { Mail, Calendar as CalendarIcon, ArrowRight } from "lucide-react";

const formatNumber = (n: number) => n.toLocaleString("en-US");
const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const startOfMonthISO = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0)).toISOString();
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { merchant, credits, loading } = useMerchant();
  const [emailsSent, setEmailsSent] = useState<number>(0);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!merchant) return;
    let active = true;
    (async () => {
      setStatsLoading(true);
      const { count } = await epicengageSupabasev1
        .from("email_sends")
        .select("id", { count: "exact", head: true })
        .eq("merchant_id", merchant.id)
        .gte("sent_at", startOfMonthISO());
      if (!active) return;
      setEmailsSent(count ?? 0);
      setStatsLoading(false);
    })();
    return () => { active = false; };
  }, [merchant]);

  const remaining = credits ? Math.max(0, (credits.credits_total ?? 0) - (credits.credits_used ?? 0)) : 0;

  return (
      <div className="px-10 py-10">
        <header className="mb-10">
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome back{merchant?.name ? `, ${merchant.name}` : ""}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Here's your email performance</p>
        </header>

        {/* Stat cards */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <article className="rounded-lg border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Credits remaining</p>
            <p className="text-3xl font-semibold mt-2 tracking-tight">
              {loading ? "—" : formatNumber(remaining)}
            </p>
            <p className="text-xs text-muted-foreground mt-2">Resets {formatDate(credits?.reset_at ?? null)}</p>
          </article>

          <article className="rounded-lg border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Emails sent this month</p>
            <p className="text-3xl font-semibold mt-2 tracking-tight">
              {statsLoading ? "—" : formatNumber(emailsSent)}
            </p>
            <p className="text-xs text-muted-foreground mt-2">Across all campaigns</p>
          </article>

          <article className="rounded-lg border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Est. revenue driven</p>
            <p className="text-3xl font-semibold mt-2 tracking-tight">$0</p>
            <p className="text-xs text-muted-foreground mt-2">
              opens × avg order value — configure in settings
            </p>
          </article>
        </section>

        {/* Action cards */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <article className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                <Mail className="h-4 w-4" />
              </div>
              <h2 className="text-base font-semibold">Send a Campaign</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Blast an email to your customers now.
            </p>
            <Button onClick={() => navigate("/clientpanel/enagev1/campaigns")}>
              Create Campaign
              <ArrowRight className="h-4 w-4" />
            </Button>
          </article>

          <article className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                <CalendarIcon className="h-4 w-4" />
              </div>
              <h2 className="text-base font-semibold">View AI Calendar</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              See upcoming events and get campaign ideas.
            </p>
            <Button variant="outline" onClick={() => navigate("/clientpanel/enagev1/calendar")}>
              Open Calendar
              <ArrowRight className="h-4 w-4" />
            </Button>
          </article>
        </section>
      </div>
  );
};

export default Dashboard;
